import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useEffect, useState } from "react";
import Spinner from "../Spinner";

interface CheckoutFormProps {
  userInfo: any;
  clientSecret: string;
  skipPayment: boolean;
  next: () => void;
  setHeight: (height: any) => void;
}

export const CheckoutForm: React.FC<CheckoutFormProps> = ({
  clientSecret,
  userInfo,
  skipPayment,
  next,
  setHeight,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [email, setEmail] = useState(userInfo.email);
  const [emailFocus, setEmailFocus] = useState(false);
  const [name, setName] = useState(userInfo.fullName);
  const [nameFocus, setNameFocus] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent?.status) {
        case "succeeded":
          setMessage("Payment succeeded!");
          break;
        case "processing":
          setMessage("Your payment is processing.");
          break;
        case "requires_payment_method":
          // setMessage("Your payment was not successful, please try again.");
          break;
        default:
          setMessage("Something went wrong.");
          break;
      }
    });
  }, [stripe]);

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (skipPayment) {
      next();
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: "http://localhost:3000",
        payment_method_data: {
          billing_details: {
            email,
            name,
          },
        },
      },
      redirect: "if_required",
    });

    if (
      error &&
      (error.type === "card_error" || error.type === "validation_error")
    ) {
      setMessage(error.message || "");
      setHeight(700)
    } else if (error) {
      setMessage("An unexpected error occurred.");
      setHeight(700)
    } else if (paymentIntent && paymentIntent.status === "succeeded") {
      localStorage.removeItem("checkoutId");
      localStorage.removeItem("basket");
      setHeight(650)
      next();
    }

    setIsLoading(false);
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit} style={formStyle}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          // padding: 10,
          color: "#000000",
          fontSize: "16px",
          fontFamily: "GT Planar Medium",
        }}
      >

        <div style={{
          overflowX: 'clip',
          overflowY: 'scroll',
          paddingLeft: 24,
          paddingRight: 24,
        }}>
                  {
          <div
            style={{
              display: "flex",
              gap: 10,
              paddingTop: 24,
              flexDirection: "column",
              width: "100%",
            }}
          >
            <label style={labelStyle}>Email</label>
            <input
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              disabled={userInfo.email}
              placeholder={"Your email address"}
              value={email}
              style={searchInputStyle(emailFocus)}
              onFocus={() => {
                setEmailFocus(true);
              }}
              onBlur={() => setEmailFocus(false)}
            />
            <br />
          </div>
        }
        {
          <div
            style={{
              display: "flex",
              gap: 10,
              flexDirection: "column",
              width: "100%",
              height: "60%",
            }}
          >
            <label style={labelStyle}>Full Name</label>
            <input
              onChange={(e) => {
                setName(e.target.value);
              }}
              disabled={userInfo.fullName}
              placeholder={"Your full name"}
              value={name}
              style={searchInputStyle(nameFocus)}
              onFocus={() => {
                setNameFocus(true);
              }}
              onBlur={() => setNameFocus(false)}
            />
            <br />
          </div>
        }
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: "200px",
        }}>
          <PaymentElement id="payment-element" />
        </div>
        </div>

      </div>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
          paddingLeft: 24,
  paddingRight: 24,
      }}>
        <button
          style={payButtonStyle}
          disabled={isLoading || !stripe || !elements}
          id="submit"
        >
          <span id="button-text">
            {isLoading ? (
              <Spinner width="30px" height="30px" border="4px" />
            ) : (
              "Pay now"
            )}
          </span>
        </button>
        {message && (
          <div style={errorStyle} id="payment-message">
            {message}
          </div>
        )}
      </div>
    </form>
  );
};

const errorStyle: React.CSSProperties = {
  padding: 10,
  display: "flex",
  justifyContent: "center",
  fontFamily: "GT Planar Regular",
  color: "#CF1558",
  fontSize: "16px",
  paddingBottom: "20px",
};

const formStyle: React.CSSProperties = {
  minHeight: "300px",
  maxHeight: "800px"
};

const payButtonStyle: React.CSSProperties = {
  display: "flex",
  alignContent: "center",
  alignItems: "center",
  justifyContent: "center",
  paddingTop: 4,
  cursor: "pointer",
  height: 48,
  background: "linear-gradient(108.64deg, #854EFB 7.51%, #8A88FF 98.44%)",
  boxShadow:
    "0px 122.973px 98.3784px rgba(0, 0, 0, 0.07), 0px 51.3752px 41.1001px rgba(0, 0, 0, 0.0503198), 0px 27.4676px 21.9741px rgba(0, 0, 0, 0.0417275), 0px 15.3981px 12.3185px rgba(0, 0, 0, 0.035), 0px 8.17783px 6.54226px rgba(0, 0, 0, 0.0282725), 0px 3.40298px 2.72238px rgba(0, 0, 0, 0.0196802)",
  borderRadius: 12,
  border: "1.5px solid #8A88FF",
  color: "#FFFFFF",
  width: "100%",
};

const inputStyle: React.CSSProperties = {
  flex: "0 0 auto",
  paddingTop: 4,
  cursor: "pointer",
  height: 48,
  background: "linear-gradient(108.64deg, #854EFB 7.51%, #8A88FF 98.44%)",
  boxShadow:
    "0px 122.973px 98.3784px rgba(0, 0, 0, 0.07), 0px 51.3752px 41.1001px rgba(0, 0, 0, 0.0503198), 0px 27.4676px 21.9741px rgba(0, 0, 0, 0.0417275), 0px 15.3981px 12.3185px rgba(0, 0, 0, 0.035), 0px 8.17783px 6.54226px rgba(0, 0, 0, 0.0282725), 0px 3.40298px 2.72238px rgba(0, 0, 0, 0.0196802)",
  borderRadius: 12,
  border: "1.5px solid #8A88FF",
  color: "#FFFFFF",
  width: "100%",
};

const labelStyle: React.CSSProperties = {
  fontSize: "16px",
  fontFamily: "GT Planar Regular",
  color: "rgba(0, 0, 0, 0.7)",
};

const searchInputStyle = (hasFocus: boolean): React.CSSProperties => ({
  display: "flex",
  height: 48,
  background: "#FFFFFF",
  borderColor: "#FFFFFF",
  outline: "none",
  border: hasFocus ? "1.5px solid #8A88FF" : "1px solid rgba(0, 0, 0, 0.2)",
  fontFamily: "GT Planar Medium",
  borderRadius: 12,
  width: "-webkit-fill-available",
  paddingLeft: 10,
  margin: 0,
});
